/**
 * Assumed-tenant banner — renders when a platform admin is viewing the
 * platform "as" another tenant. Must never be dismissible: impersonation
 * without a visual reminder is how audit incidents happen.
 */
import { useEffect, useState } from "react";
import { Eye, X } from "lucide-react";
import api, { getAssumedTenant, setAssumedTenant } from "@/lib/api";

export default function AssumedTenantBanner() {
  const [assumed, setAssumed] = useState(getAssumedTenant());
  const [name, setName] = useState("");

  useEffect(() => {
    const on = (e) => setAssumed(e.detail?.tenantId || "");
    window.addEventListener("altara:assumed-tenant-changed", on);
    return () => window.removeEventListener("altara:assumed-tenant-changed", on);
  }, []);

  useEffect(() => {
    if (!assumed) { setName(""); return; }
    api.get("/admin/tenants")
      .then((r) => {
        const t = (r.data || []).find((x) => x.tenant_id === assumed);
        setName(t?.name || assumed);
      })
      .catch(() => setName(assumed));
  }, [assumed]);

  if (!assumed) return null;

  return (
    <div
      data-testid="assumed-tenant-banner"
      className="flex items-center justify-between gap-3 px-6 py-2 border-b"
      style={{ background: "#FEF3C71A", borderColor: "#C9A24A55" }}
    >
      <div className="flex items-center gap-2 text-[12px]" style={{ color: "#0A1F44" }}>
        <Eye className="w-3.5 h-3.5" style={{ color: "#C9A24A" }} />
        <span>
          Viewing the platform as{" "}
          <span className="font-semibold">{name || assumed}</span>. All data is filtered to that tenant.
        </span>
      </div>
      <button
        data-testid="assumed-tenant-banner-clear"
        onClick={() => { setAssumedTenant(""); setTimeout(() => window.location.reload(), 60); }}
        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-slate-300 hover:bg-white"
        style={{ color: "#0A1F44" }}
      >
        <X className="w-3 h-3" /> Revert to Altara view
      </button>
    </div>
  );
}
